import React, { useCallback } from 'react';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useGetCategoriesQuery } from '@/modules/ProcessingDefinition/Services/queries';
import { CategoryItem, ServiceItem } from '@/modules/ProcessingDefinition/Services/Services.types';

import { AddServicesModalProps } from './AddServicesModal.types';
import { AddServicesModal } from './components';
import { CHECK_STATE, useAddServicesSelection, useLoadingTracker } from './hooks';

const AddServicesModalContainer: React.FC<AddServicesModalProps> = ({ visible, onClose, onAddServices }) => {
  const { categories, isLoading: isCategoriesLoading } = useGetCategoriesQuery();
  const selection = useAddServicesSelection();
  const { isAnyLoading, startLoading, endLoading } = useLoadingTracker();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const handleToggleCategory = useCallback(
    (category: CategoryItem, allServices?: ServiceItem[]) => {
      const checkState = selection.getCategoryCheckState(category.name, category.serviceCount);
      if (checkState === CHECK_STATE.UNCHECKED || checkState === CHECK_STATE.INDETERMINATE) {
        if (allServices) {
          selection.selectAllCategory(category.name, allServices);
        }
      } else {
        selection.deselectAllCategory(category.name);
      }
    },
    [selection]
  );

  const handleToggleService = useCallback(
    (categoryType: string, service: ServiceItem) => {
      selection.toggleService(categoryType, service);
    },
    [selection]
  );

  const getCategoryCheckState = useCallback(
    (categoryType: string) => {
      const category = categories.find((c) => c.name === categoryType);
      return selection.getCategoryCheckState(categoryType, category?.serviceCount || 0);
    },
    [selection, categories]
  );

  const handleClose = useCallback(() => {
    selection.reset();
    onClose();
  }, [selection, onClose]);

  const handleSubmit = useCallback(() => {
    const selectedServices = selection.getSelectedServices();
    onAddServices(selectedServices);
    handleClose();
    handleAddPermanentSnackbar({
      text: 'Selected services were added',
      closeIcon: true,
      state: SnackbarState.positive,
    });
  }, [selection, onAddServices, handleClose, handleAddPermanentSnackbar]);

  return (
    <AddServicesModal
      visible={visible}
      categories={categories}
      isCategoriesLoading={isCategoriesLoading}
      totalSelected={selection.totalSelected}
      isAnyLoading={isAnyLoading}
      getCategoryCheckState={getCategoryCheckState}
      isServiceSelected={selection.isServiceSelected}
      onToggleCategory={handleToggleCategory}
      onToggleService={handleToggleService}
      onStartLoading={startLoading}
      onEndLoading={endLoading}
      onClose={handleClose}
      onSubmit={handleSubmit}
    />
  );
};

export default AddServicesModalContainer;
